import { type FC, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import cx from 'clsx';
import type codemirror from 'codemirror';
import { useFlushSyncCallback } from '@editor2/hooks/useFlushSyncCallback';

import { type PageProgress } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { pageProgressElement } from './styles';

import { Controlled as CodeMirror } from 'react-codemirror2';
import { useCodeMirrorStrictModeHandlers } from '@editor2/hooks/useCodeMirrorStrictModeHandlers';

import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/xml/xml';
import 'codemirror/addon/display/autorefresh.js';
import { pageProgressToPageProgressProperties } from '@editor2/mappers/element';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { elementCommonStyles } from '@editor2/styles/element';

interface Props {
	element: PageProgress;
	courseId: number;
}

const PageProgressElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };
	const initProperties = pageProgressToPageProgressProperties(element);

	// Separate handlers for each CodeMirror instance
	const activeTextHandlers = useCodeMirrorStrictModeHandlers();
	const passiveTextHandlers = useCodeMirrorStrictModeHandlers();
	const bottomTextHandlers = useCodeMirrorStrictModeHandlers();

	const debouncedDispatch = useDebouncedDispatch();

	const [pageProgressProperties, setPageProgressProperties] = useState({ ...initProperties });

	useEffect(() => {
		const updatedPageProgressProperties = pageProgressToPageProgressProperties(element);

		if (JSON.stringify(element) !== JSON.stringify(pageProgressProperties)) {
			setPageProgressProperties({
				...pageProgressProperties,
				...updatedPageProgressProperties
			});
		}
	}, [element]);

	const codeMirrorOptions = {
		mode: 'text/html',
		lineNumbers: true,
		lineWrapping: true,
		autoRefresh: true,
		extraKeys: {
			Tab: false,
			'Shift-Tab': false
		},
		// Configure history for better undo/redo preservation
		undoDepth: 300,
		historyEventDelay: 2000
	} satisfies codemirror.EditorConfiguration;

	const handleChange = (value) => {
		setPageProgressProperties({ ...pageProgressProperties, ...value });
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	// Create flushSync versions of specific handlers
	const syncHandleActiveTextChange = useFlushSyncCallback((value: string) =>
		handleChange({ active_prediction_text: value })
	);
	const syncHandlePassiveTextChange = useFlushSyncCallback((value: string) =>
		handleChange({ passive_prediction_text: value })
	);
	const syncHandleBottomTextChange = useFlushSyncCallback((value: string) =>
		handleChange({ active_prediction_bottom_text: value })
	);

	return (
		<Form className={cx(pageProgressElement, elementCommonStyles)}>
			<Form.Group>
				<Form.Label>Family ID of target page upon which to base estimate</Form.Label>
				<Form.Control
					value={pageProgressProperties.target_page_family_id}
					onChange={(e) => handleChange({ target_page_family_id: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>
					Minimum time spent (in minutes) on target page before active prediction text is shown
				</Form.Label>
				<Form.Control
					value={pageProgressProperties.min_time_spent_estimation_threshold}
					onChange={(e) => handleChange({ min_time_spent_estimation_threshold: e.target.value })}
				/>
			</Form.Group>

			<Form.Group className="block">
				<Form.Label>
					{`Active Prediction Text - can contain HTML and {target_page.page_number} ` +
						`and {target_page.time_spent.in_whole_minutes} placeholders`}
				</Form.Label>
				<CodeMirror
					ref={activeTextHandlers.wrapperRef}
					value={pageProgressProperties.active_prediction_text}
					options={codeMirrorOptions}
					onBeforeChange={(editor, data, value) => syncHandleActiveTextChange(value)}
					editorDidMount={activeTextHandlers.handleEditorDidMount}
					editorWillUnmount={activeTextHandlers.handleEditorWillUnmount}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>
					{`Passive Prediction Text for students who haven’t met the minimum time spent` +
						` on target page - can contain HTML`}
				</Form.Label>
				<CodeMirror
					ref={passiveTextHandlers.wrapperRef}
					value={pageProgressProperties.passive_prediction_text}
					options={codeMirrorOptions}
					onBeforeChange={(editor, data, value) => syncHandlePassiveTextChange(value)}
					editorDidMount={passiveTextHandlers.handleEditorDidMount}
					editorWillUnmount={passiveTextHandlers.handleEditorWillUnmount}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>
					{`Active Prediction Bottom Text - can contain HTML and {my_progress_link}`}
				</Form.Label>
				<CodeMirror
					ref={bottomTextHandlers.wrapperRef}
					value={pageProgressProperties.active_prediction_bottom_text}
					options={codeMirrorOptions}
					onBeforeChange={(editor, data, value) => syncHandleBottomTextChange(value)}
					editorDidMount={bottomTextHandlers.handleEditorDidMount}
					editorWillUnmount={bottomTextHandlers.handleEditorWillUnmount}
				/>
			</Form.Group>
		</Form>
	);
};

export default PageProgressElement;
